import React, { useState, useEffect, useRef } from 'react';
import { CategoryConfig } from '../HomeTop/CategoryCard';
import { EnumConfig } from '../HomeTop/EnumCard';
import { RecommendConfig } from '../HomeTop/RecommendCard';
import { AdConfig } from '../AdGod/GodBox';
import { SpmKeys } from '../../models/spmDict';
import { RegionKeys, testRegionDict } from '../../models/regionDict';
import { BatchRegionConfig } from '../../models/apis/batchAPI';
import { CisBatchConfig, CisData } from '../../models/apis/batchCisApi';
import { cisItemsWasher } from '../../utils/cisItemsWasher';
import { genCisBatchConfig, genBatchConfig, sleep } from '../../utils';
import { splitChunks } from '../../utils/splitChunks';
import { jsonPost } from '../../utils/http';
import { isTest } from '../../utils/isProd';
import { getCookie } from '../../utils/cookieHelper';
import { spmGetEcode, genRands, domDidChange} from '../../utils/spmHelper';
import { SubjectPlaceHolderConfig, SubjectPlaceHolder } from '../SubjectPlaceHolder';
import { SingleScreenConfigs, SingleScreen } from './index';
import { isInView } from 'src/utils/isInView';
import { useEffectAsync } from '../../hooks';
// Interfaces
interface CategoryWrapperConfig extends Omit<CategoryConfig, 'source'> {
    // This `source` field stands for the region id that we request.
    // The first is the main feeds and the second is the OS channel
    // contents.
    // source: SpmKeys[],
    source: any[],
}

interface EnumWrapperConfig extends Omit<EnumConfig, 'source'> {
    source: SpmKeys[],
}

interface RecommendWrapperConfig extends Omit<RecommendConfig, 'source'> {
    source: SpmKeys,
}

interface SubjectPlaceHolderWrapperConfig extends Omit<SubjectPlaceHolderConfig, 'source'> {
    source: any
    // source: SpmKeys,
}

interface WrapperConfig {
    channelConfigs: CategoryWrapperConfig[],
    enumConfigs: EnumWrapperConfig[],
    recommendConfigs: RecommendWrapperConfig[],
    rectAdConfig?: {
        regionId: RegionKeys,
        placeholder: SubjectPlaceHolderWrapperConfig
        spm: SpmKeys,
    },
    longAdConfig?: {
        regionId: RegionKeys,
        spm: SpmKeys,
    },
}

interface ConfigArgs extends WrapperConfig {
    store: any
}

/**
 * Request for batch feeds
 */
const requestRegions = (regions: BatchRegionConfig[]) => {
    const baseUrl = isTest ? 'https://d1.m.sohu.com/integration-api/batch/web' : 'https://v2.sohu.com/integration-api/batch/web';
    const reqRegions = regions.map(region => ({
        ...region,
        regionId: isTest ? testRegionDict[region.regionId] : region.regionId,
    }));
    return jsonPost(baseUrl, {
        regions: reqRegions,
        client: 3,
        suv: getCookie('SUV'),
        pvid: spmGetEcode(),
        spm_e: spmGetEcode(),
        requestid: `${getCookie('SUV')}_${+new Date()}`,
        refer: document.referrer,
        user_agent: navigator.userAgent,
        adapter: { name: 'pc' },
    }).then(res => {
        const body = (res as any).body;
        const data: { data: any } = { data: {} };
        regions.forEach(region => {
            const id = region.regionId;
            const reqId = isTest ? testRegionDict[id] : id;
            data.data[id] = (body.data[reqId] && body.data[reqId].data) ? body.data[reqId] : { data: [] };
        });
        return data.data;
    });
};

// Utils
/**
 * Request for batch feeds
 */
const requestCis = (spms: CisBatchConfig[]) => {
    const baseUrl = isTest ? 'http://d1.m.sohu.com/cisapi/cis/feeds' : 'https://cis.sohu.com/cis/feeds';
    const spmList = spms.map(batch => batch.spm.split('.')[2]);
    return jsonPost(baseUrl, {
        sceneParam: spms,
        client: 3,
        suv: getCookie('SUV'),
        pvid: spmGetEcode(),
        spm_e: spmGetEcode(),
        clientType: 3,
        requestid: +new Date() + genRands(),
        refer: document.referrer,
        user_agent: navigator.userAgent,
    }).then(res => {
        const originData = res.body;
        const result:CisData = {};
        spmList.forEach(id => {
            result[id] = { data: [] };
        });
        spmList.forEach((spm: string) => {
            const spmCode = `smpc.home-classic.${spm}`;
            const feedData = (originData[spmCode] && originData[spmCode].data) || [];
            const cleanFeedData = cisItemsWasher(feedData);
            result[spm] = {
                data: cleanFeedData,
            };
        });
        return result;
    });
};

/**
 * generate `<SingleScreen />`'s config by store data
 *
 * @param data
 */
const genConfigs = (data: ConfigArgs): SingleScreenConfigs => {
    const { channelConfigs, enumConfigs, recommendConfigs, rectAdConfig, longAdConfig, store } = data;
    const getBatch = (id: SpmKeys) => {
        if (store[id]) {
            return store[id]!.data;
        }
        return [];
    };
    const tempChannelConfigs: CategoryConfig[] = channelConfigs.map(config => {
        const osData = config.source[1] ? getBatch(config.source[1]) : [];
        const mainFeeds = getBatch(config.source[0]);
        return {
            ...config,
            source: osData.concat(mainFeeds),
        };
    });
    const tempEnumConfigs: EnumConfig[] = enumConfigs.map(config => {
        const source = config.source.map(id => getBatch(id));
        return {
            ...config,
            source,
        };
    });
    const genAdConfig = (width: number, height: number, dataSource: any[], adConfig: { spm: string, placeholder?: SubjectPlaceHolderWrapperConfig}): AdConfig => {
        const spmCode = adConfig ? adConfig.spm : '';
        const { placeholder } = adConfig;
        const res: AdConfig = { width, height, dataSource, spmCode};
        if (placeholder) {
            const config = { ...placeholder, source: getBatch(placeholder.source) };
            if (width === 300) { res.children = (<SubjectPlaceHolder {...config} />); }
        }
        return res;
    };
    return {
        channelConfigs: tempChannelConfigs,
        enumConfigs: tempEnumConfigs,
        recommendConfigs: recommendConfigs.map(config => ({ ...config, source: getBatch(config.source) })),
        rectAdConfig: rectAdConfig ? [genAdConfig(300, 250, rectAdConfig && getBatch(rectAdConfig && rectAdConfig!.spm) || [], rectAdConfig)] : [],
        longAdConfig: (longAdConfig && getBatch(longAdConfig.spm).length) ? [genAdConfig(1180, 100, getBatch(longAdConfig.spm), longAdConfig)] : [],
    };
};

/**
 * Get Batch datas by pre-config
 *
 * @param props {WrapperConfig} - pre-configs
 * @param setState - set store
 */
// an
const requestStore = async (props: WrapperConfig, setState: (arg: any) => any) => {
    const { channelConfigs, enumConfigs, recommendConfigs, rectAdConfig, longAdConfig } = props;
    const batchConfigs: CisBatchConfig[] = [];
    const sspConfigs: any[] = [];
        channelConfigs.forEach(config => {
            // ssp兼容未下线的内容
            if(typeof config.source[0] === 'number') {
                sspConfigs.push(genBatchConfig(config.source[0] as RegionKeys, 4));
                if (config.source[1]) {
                    sspConfigs.push(genBatchConfig(config.source[1], 2));
                }
            } else {
                batchConfigs.push(genCisBatchConfig(config.source[0], 4));
            }
        });
        enumConfigs.forEach(config => {
            config.source.forEach((id) => {
                batchConfigs.push(genCisBatchConfig(id, 6 / config.source.length));
            });
        });
        recommendConfigs.forEach(config => {
            batchConfigs.push(genCisBatchConfig(config.source, 2));
        });
        // if (rectAdConfig && rectAdConfig.placeholder) { batchConfigs.push(genCisBatchConfig(rectAdConfig.placeholder.source, 3)); }
        // if (rectAdConfig) { batchConfigs.push(genCisBatchConfig(rectAdConfig.spm, 1, 1)); }
        // if (longAdConfig) { batchConfigs.push(genCisBatchConfig(longAdConfig.spm, 1, 1)); }
        if (rectAdConfig && rectAdConfig.placeholder) { batchConfigs.push(genCisBatchConfig(rectAdConfig.placeholder.source, 3)); }
        if (rectAdConfig) { sspConfigs.push(genBatchConfig(rectAdConfig.regionId, 1, 1)); }
        if (longAdConfig) { sspConfigs.push(genBatchConfig(longAdConfig.regionId, 1, 1)); }
        // getConfig
        // split into chunks
        const spmChunks = splitChunks(batchConfigs, 10);
        const sspChunks = splitChunks(sspConfigs, 10);
        const batchData = await Promise.all([
            ...spmChunks.map(async spms => await requestCis(spms)),
            ...sspChunks.map(async regions => await requestRegions(regions)),
        ]);
        setState(batchData.reduce((a, b) => ({ ...a, ...b }), {}));
};

export const SingleScreenWrapper = (props: WrapperConfig) => {
    const [store, setStore] = useState<any>({});
    const initConfigs = genConfigs({ ...props, store });
    const [configs, setConfigs] = useState<SingleScreenConfigs>(initConfigs);
    const ref = useRef(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Get datas
    useEffect(() => {
        const handleScroll = () => {
            if (!ref.current || Object.keys(store).length || isLoading) { return; }
            const element = (ref.current as unknown as HTMLElement);
            if (isInView(element)) {
                setIsLoading(true);
                window.removeEventListener('scroll', handleScroll);
                requestStore(props, setStore);
            }
        };
        if (!Object.keys(store).length) { window.addEventListener('scroll', handleScroll); }
        // Must handle at once because user might enter the pageat the middle.
        handleScroll();
        return () => {
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    // Generate configs by stores
    useEffect(() => {
        const config = genConfigs({ ...props, store });
        setConfigs(config);
        setTimeout(() => {
            const evt = 'LOAD_GOD_AD_ASM';
            if (typeof Event === 'function') {
                document.dispatchEvent(new Event(evt));
            } else {
                const event = document.createEvent('Event');
                event.initEvent(evt,true,false);
                document.dispatchEvent(event);
            }
        }, 100);
    }, [store]);
    // spm
    useEffectAsync(async () => {
        await sleep(200);
        domDidChange();
    }, [store]);
    return (
        <div ref={ref}>
            <SingleScreen
                {...configs}
            />
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/components/SingleScreen/requestCisWrapper.tsx